<script lang="ts" setup>
import PopOver from "@/components/utils/PopOver.vue";
import BaseLink from "~/components/buttons/BaseLink.vue";
import auth from "~/services/AuthService";
import eventBus from "~/event-bus";
import { useTransactionConfirmationNotifications } from "@/composables/useTransactionConfirmationNotifications";

const props = withDefaults(
  defineProps<{
    mobile?: boolean;
    variant?: "compact" | "bar";
  }>(),
  {
    mobile: false,
    variant: "compact",
  },
);

const route = useRoute();
const dismissed = ref(false);
const {
  loading,
  pendingCount,
  items,
  fetchPendingConfirmations,
  startPolling,
  stopPolling,
} = useTransactionConfirmationNotifications();
const displayCount = computed(() =>
  pendingCount.value > 9 ? "9+" : pendingCount.value,
);
const isLoggedUser = computed(() => auth.isUserLogged());
const hasPendingConfirmations = computed(() => pendingCount.value > 0);
const isConfirmationRoute = computed(() =>
  ["order-confirmation", "order-confirmations"].includes(
    String(route.name || ""),
  ),
);
const showBar = computed(
  () =>
    props.variant === "bar" &&
    isLoggedUser.value &&
    hasPendingConfirmations.value &&
    !dismissed.value &&
    !isConfirmationRoute.value,
);
const summaryTitle = computed(() =>
  hasPendingConfirmations.value
    ? `${displayCount.value} confirmacione${pendingCount.value > 1 ? "s" : ""} pendiente${pendingCount.value > 1 ? "s" : ""}`
    : "Sin confirmaciones pendientes",
);
const summaryText = computed(() =>
  hasPendingConfirmations.value
    ? "Tu pago ya fue confirmado. Revísalo para cerrar este aviso."
    : "Aquí verás tus pagos confirmados cuando necesiten tu atención.",
);

const popOverId = computed(
  () => "pop-over-notifications" + Math.random().toString(36).substring(7),
);

const confirmationRoute = (transactionId: string) => ({
  name: "order-confirmation",
  params: { id: transactionId },
});

const notificationCenterRoute = computed(() => ({
  name: "order-confirmations",
}));

const handleRefreshNotifications = async () => {
  await fetchPendingConfirmations();
};

const dismissBar = () => {
  dismissed.value = true;
};

watch(
  () => route.fullPath,
  async () => {
    await handleRefreshNotifications();
  },
);

onMounted(async () => {
  await handleRefreshNotifications();
  startPolling();
  eventBus.on("refresh-confirmation-notifications", handleRefreshNotifications);
});

onBeforeUnmount(() => {
  stopPolling();
  eventBus.off(
    "refresh-confirmation-notifications",
    handleRefreshNotifications,
  );
});
</script>

<template>
  <div
    v-if="showBar"
    :class="{
      'px-5 pb-3': variant === 'bar' && !mobile,
      'pt-1 pb-2': variant === 'bar' && mobile,
    }"
  >
    <NuxtLink
      v-if="showBar"
      :to="notificationCenterRoute"
      class="notification-bar text-decoration-none"
      :class="{
        'notification-bar-pending': hasPendingConfirmations,
        'notification-bar-idle': !hasPendingConfirmations,
      }"
      aria-label="Abrir centro de confirmaciones de pago"
      title="Confirmaciones de pago"
    >
      <span class="notification-bar-main">
        <span class="notification-bar-icon-wrap">
          <i class="bi bi-patch-check-fill notification-bar-icon"></i>
        </span>
        <span class="notification-bar-copy">
          <span class="notification-bar-label">Confirmaciones de pago</span>
          <span class="notification-bar-title">{{ summaryTitle }}</span>
          <span class="notification-bar-text">{{ summaryText }}</span>
        </span>
      </span>
      <span class="notification-bar-side">
        <span
          class="notification-bar-badge"
          :class="{
            'notification-bar-badge-active': hasPendingConfirmations,
            'notification-bar-badge-idle': !hasPendingConfirmations,
          }"
        >
          {{ displayCount }}
        </span>
        <button
          type="button"
          class="notification-bar-dismiss"
          aria-label="Ocultar aviso de confirmaciones"
          title="Ocultar temporalmente"
          @click.prevent.stop="dismissBar"
        >
          <i class="bi bi-x-lg"></i>
        </button>
        <i class="bi bi-chevron-right notification-bar-arrow"></i>
      </span>
    </NuxtLink>
    <NuxtLink
      v-else-if="
        variant !== 'bar' && mobile && isLoggedUser && !isConfirmationRoute
      "
      :to="notificationCenterRoute"
      class="notification-mobile-link text-white p-2 h-100 position-relative"
      aria-label="Confirmaciones de pago pendientes"
      title="Confirmaciones de pago"
    >
      <span class="notification-mobile-icon-wrap">
        <i class="bi bi-patch-check-fill notification-mobile-icon"></i>
        <span
          v-if="pendingCount > 0"
          class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-warning text-dark notification-badge-mobile"
        >
          {{ displayCount }}
        </span>
      </span>
      <span class="notification-mobile-text">Pagos</span>
    </NuxtLink>
    <pop-over
      v-else-if="variant !== 'bar' && isLoggedUser && !isConfirmationRoute"
      :id="popOverId"
    >
      <template #buttonText>
        <button type="button" class="notification-trigger-btn">
          <span class="notification-trigger-icon-wrap">
            <i class="bi bi-patch-check-fill notification-trigger-icon"></i>
            <span
              v-if="pendingCount > 0"
              class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-warning text-dark notification-badge"
            >
              {{ displayCount }}
            </span>
          </span>
          <span class="notification-trigger-copy">
            <span class="notification-trigger-label">Confirmaciones</span>
            <span class="notification-trigger-subtext">
              {{
                pendingCount > 0
                  ? `${displayCount} pendiente${pendingCount > 1 ? "s" : ""}`
                  : "Pagos confirmados"
              }}
            </span>
          </span>
        </button>
      </template>
      <template #title>Confirmaciones de pago</template>
      <template #default>
        <div class="notification-popover">
          <div
            v-if="loading"
            class="notification-state-box small text-secondary"
          >
            <span class="spinner-border spinner-border-sm text-danger"></span>
            <span>Cargando confirmaciones...</span>
          </div>
          <template v-else-if="items.length">
            <div class="notification-popover-header">
              <span class="notification-popover-header-label">
                Pagos confirmados por revisar
              </span>
              <span class="badge text-bg-danger">{{ pendingCount }}</span>
            </div>
            <NuxtLink
              v-for="item in items"
              :key="item.transaction_id"
              :to="confirmationRoute(item.transaction_id)"
              class="notification-item text-decoration-none"
            >
              <div class="notification-item-header">
                <span class="notification-item-folio">
                  {{ item.folio || `Pedido #${item.transaction_id}` }}
                </span>
                <span class="notification-item-total">{{
                  item.total_text
                }}</span>
              </div>
              <div class="notification-item-text">
                Tu pago ya fue confirmado. Revisa esta confirmación.
              </div>
            </NuxtLink>
            <base-link
              :to="notificationCenterRoute"
              class="link-underline link-underline-opacity-0 text-danger fs-9 text-end fw-semibold"
            >
              Ver todas
            </base-link>
          </template>
          <div v-else class="notification-state-box d-flex flex-column">
            <p class="fs-9 mb-2 text-secondary">
              No tienes confirmaciones pendientes por revisar.
            </p>
            <base-link
              :to="notificationCenterRoute"
              class="link-underline link-underline-opacity-0 text-danger fs-9 text-end"
            >
              Ir al centro de confirmaciones
            </base-link>
          </div>
        </div>
      </template>
    </pop-over>
  </div>
</template>

<style scoped lang="scss">
.notification-bar {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1.15rem;
  border-radius: 1rem;
  border: 1px solid rgba(220, 13, 21, 0.12);
  background: linear-gradient(135deg, #fff8f8 0%, #ffffff 100%);
  box-shadow: 0 0.5rem 1.1rem rgba(15, 23, 42, 0.05);
  color: inherit;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease,
    background-color 0.2s ease;
}

.notification-bar:hover,
.notification-bar:focus-visible {
  transform: translateY(-1px);
  box-shadow: 0 0.7rem 1.25rem rgba(15, 23, 42, 0.08);
}

.notification-bar-pending {
  border-color: rgba(220, 13, 21, 0.18);
  background: linear-gradient(135deg, #fff5f5 0%, #ffffff 100%);
}

.notification-bar-idle {
  border-color: rgba(15, 23, 42, 0.08);
  background: linear-gradient(135deg, #fafbfc 0%, #ffffff 100%);
}

.notification-bar-main {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.9rem;
}

.notification-bar-icon-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 1rem;
  background: rgba(220, 13, 21, 0.1);
  color: var(--bs-danger);
  flex-shrink: 0;
}

.notification-bar-icon {
  font-size: 1.2rem;
}

.notification-bar-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.notification-bar-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--bs-danger);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.notification-bar-title {
  font-size: 0.98rem;
  font-weight: 700;
  color: var(--bs-dark);
  line-height: 1.2;
}

.notification-bar-text {
  font-size: 0.84rem;
  color: var(--bs-secondary-color);
  line-height: 1.35;
}

.notification-bar-side {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.notification-bar-badge {
  min-width: 2.2rem;
  padding: 0.45rem 0.7rem;
  border-radius: 999px;
  text-align: center;
  font-size: 0.82rem;
  font-weight: 700;
}

.notification-bar-badge-active {
  color: #3d2d00;
  background: #ffe08a;
  box-shadow: 0 0.55rem 1rem rgba(255, 193, 7, 0.26);
}

.notification-bar-badge-idle {
  color: var(--bs-secondary-color);
  background: var(--bs-tertiary-bg);
}

.notification-bar-arrow {
  font-size: 0.95rem;
  color: var(--bs-secondary-color);
}

.notification-bar-dismiss {
  width: 2rem;
  height: 2rem;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--bs-secondary-color);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
}

.notification-bar-dismiss:hover,
.notification-bar-dismiss:focus-visible {
  background: rgba(15, 23, 42, 0.08);
  color: var(--bs-dark);
}

.notification-trigger-btn {
  height: 100%;
  width: 100%;
  border: 0;
  background: var(--bs-tertiary-bg);
  color: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0 0.85rem;
  transition:
    background-color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.notification-trigger-btn:hover,
.notification-trigger-btn:focus-visible {
  background: #f8dcde;
  transform: translateY(-1px);
}

.notification-trigger-icon-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.2rem;
  height: 2.2rem;
  border-radius: 0.85rem;
  background: rgba(220, 13, 21, 0.1);
  color: var(--bs-danger);
  flex-shrink: 0;
}

.notification-trigger-icon {
  font-size: 1.1rem;
}

.notification-trigger-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.15;
}

.notification-trigger-label {
  color: var(--bs-dark);
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
}

.notification-trigger-subtext {
  color: var(--bs-secondary-color);
  font-size: 0.72rem;
}

.notification-badge {
  min-width: 1.35rem;
  padding-inline: 0.38rem;
  box-shadow: 0 0.35rem 0.75rem rgba(0, 0, 0, 0.18);
  font-size: 0.7rem;
  animation: notification-pulse 1.8s ease-in-out infinite;
}

.notification-badge-mobile {
  min-width: 1.2rem;
  padding-inline: 0.34rem;
  box-shadow: 0 0.35rem 0.75rem rgba(0, 0, 0, 0.18);
  font-size: 0.65rem;
  animation: notification-pulse 1.8s ease-in-out infinite;
}

.notification-popover {
  min-width: 19rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.notification-popover-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.notification-popover-header-label {
  color: var(--bs-secondary-color);
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.notification-state-box {
  padding: 0.9rem;
  border: 1px solid var(--bs-border-color);
  border-radius: 0.9rem;
  background: var(--bs-tertiary-bg);
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.notification-item {
  display: block;
  padding: 0.75rem;
  border: 1px solid var(--bs-border-color);
  border-radius: 0.85rem;
  background: var(--bs-white);
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease,
    transform 0.2s ease;
}

.notification-item:hover {
  border-color: rgba(220, 13, 21, 0.22);
  background: var(--bs-tertiary-bg);
  transform: translateY(-1px);
}

.notification-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
}

.notification-item-folio {
  color: var(--bs-dark);
  font-size: 0.86rem;
  font-weight: 700;
}

.notification-item-total {
  color: var(--bs-danger);
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
}

.notification-item-text {
  color: var(--bs-secondary-color);
  font-size: 0.8rem;
  line-height: 1.45;
}

.notification-mobile-link {
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.15rem;
  min-width: 3.2rem;
}

.notification-mobile-icon-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.notification-mobile-icon {
  font-size: 1.25rem;
}

.notification-mobile-text {
  font-size: 0.58rem;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

@keyframes notification-pulse {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(0, 0) scale(1.06);
  }
}

@media (max-width: 991.98px) {
  .notification-bar {
    padding: 0.8rem 0.95rem;
    border-radius: 0.95rem;
    gap: 0.75rem;
  }

  .notification-bar-icon-wrap {
    width: 2.6rem;
    height: 2.6rem;
    border-radius: 0.9rem;
  }

  .notification-bar-title {
    font-size: 0.92rem;
  }

  .notification-bar-text {
    font-size: 0.78rem;
  }

  .notification-bar-side {
    gap: 0.55rem;
  }
}
</style>
